import { useRef, useState } from "react";

/**
 * Simple before/after image comparison slider.
 * Drag the handle (or click anywhere on the image) to reveal more of the
 * "after" side. Used on Portfolio → Redraw and on the Renew Old Plans strip.
 */
export default function BeforeAfterSlider({ before, after, alt = "" }) {
  const [pos, setPos] = useState(55);
  const containerRef = useRef(null);
  const draggingRef = useRef(false);

  const setFromClientX = (clientX) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const rel = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.max(0, Math.min(100, rel)));
  };

  const onPointerDown = (e) => {
    draggingRef.current = true;
    e.currentTarget.setPointerCapture(e.pointerId);
    setFromClientX(e.clientX);
  };
  const onPointerMove = (e) => {
    if (!draggingRef.current) return;
    setFromClientX(e.clientX);
  };
  const onPointerUp = (e) => {
    draggingRef.current = false;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
  };

  return (
    <div
      ref={containerRef}
      className="relative select-none rounded-2xl overflow-hidden border border-stone-200 bg-[#FAF7F2] aspect-[4/3] w-full touch-none cursor-ew-resize"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      data-testid="before-after-slider"
    >
      {/* After (full width, on bottom) */}
      <img
        src={after}
        alt={`${alt} — after`}
        draggable={false}
        className="absolute inset-0 w-full h-full object-contain"
      />
      {/* Before (clipped to the left portion via inline width) */}
      <div
        className="absolute inset-0 overflow-hidden"
        style={{ width: `${pos}%` }}
      >
        <img
          src={before}
          alt={`${alt} — before`}
          draggable={false}
          className="absolute inset-0 h-full object-contain"
          style={{ width: `${(100 / pos) * 100}%`, maxWidth: "none" }}
        />
      </div>

      {/* Labels */}
      <span
        className="absolute top-3 left-3 bg-amber-700/90 text-white px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider pointer-events-none"
        data-testid="before-after-label-before"
      >
        Before
      </span>
      <span
        className="absolute top-3 right-3 bg-teal-700/90 text-white px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider pointer-events-none"
        data-testid="before-after-label-after"
      >
        After
      </span>

      {/* Handle */}
      <div
        className="absolute top-0 bottom-0 pointer-events-none"
        style={{ left: `${pos}%`, transform: "translateX(-50%)" }}
      >
        <div className="w-0.5 h-full bg-white shadow-[0_0_0_1px_rgba(15,23,42,0.15)]" />
        <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 h-10 w-10 rounded-full bg-white shadow-md border border-stone-200 flex items-center justify-center">
          <div className="flex items-center gap-0.5 text-stone-700">
            <span className="text-xs">◀</span>
            <span className="text-xs">▶</span>
          </div>
        </div>
      </div>
    </div>
  );
}
